import { useUser } from 'apps/sigma2-ui/src/state/user';
import { useAsyncCallback } from 'apps/sigma2-ui/src/lib/hooks/useAsyncCallback';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { ErrorFn } from 'apps/sigma2-ui/src/lib/read-write-patterns';
import { ReadCompleteFn } from 'apps/sigma2-ui/src/lib/read-write-patterns';
import { ReadFn } from 'apps/sigma2-ui/src/lib/read-write-patterns';
import { S2SafeParsable } from 'libs/end-to-end/src/interfaces';
import { useMemo } from 'react';

interface ReadFnHook<TRead> {
  readFn: ReadFn<TRead>;
  isReadyToRead: boolean;
}

export const useReadFn = <TRead>({
  getUrlFn,
  canRead,
  responseSchema,
}: {
  getUrlFn: () => string | null;
  canRead: boolean;
  responseSchema: S2SafeParsable<unknown, TRead>;
}): ReadFnHook<TRead> => {
  const { myUserStatus, userAuthenticatedApiCall } = useUser();
  const isReadyToRead = myUserStatus === 'logged_in' && canRead;

  const readFn = useAsyncCallback(
    async (
      isAlive: IsAliveFn,
      complete: ReadCompleteFn<TRead>,
      error: ErrorFn,
    ) => {
      if (!isAlive() || !isReadyToRead) {
        return;
      }

      const url = getUrlFn();

      if (!url) {
        return;
      }

      const result = await userAuthenticatedApiCall({
        responseSchema,
        url,
        requestOptions: {
          method: 'GET',
        },
      });

      if (!isAlive() || !result) {
        return;
      }

      if (result.resultType === 'err') {
        error(result.error);
        return;
      }

      complete(result.value);
    },
    [isReadyToRead, userAuthenticatedApiCall, responseSchema, getUrlFn],
  );

  return useMemo(() => {
    return {
      readFn,
      isReadyToRead,
    };
  }, [isReadyToRead, readFn]);
};
